import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryKeys } from '@/shared/api';

import type { GroupedKind, GroupList, WorkspaceGroup } from './types';

export function useGroups(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.groups.list(workspaceId),
    queryFn: ({ signal }) => api.get<GroupList>(`/v1/workspaces/${workspaceId}/groups`, { signal }),
  });
}

/** The groups of several workspaces (the Home page), under the same keys as `useGroups`. */
export function useGroupsOf(workspaceIds: readonly string[]) {
  return useQueries({
    queries: workspaceIds.map((id) => ({
      queryKey: queryKeys.groups.list(id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<GroupList>(`/v1/workspaces/${id}/groups`, { signal }),
    })),
    // one stable value for the whole set: the page re-derives its places only when data changed
    combine: (results) => ({
      data: results.map((result) => result.data),
      pending: results.some((result) => result.isPending),
    }),
  });
}

/** A group operation can move anything inside: every catalog list of the workspace is read again. */
function useRefresh(workspaceId: string) {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.groups.list(workspaceId) }),
      client.invalidateQueries({ queryKey: queryKeys.boards.list(workspaceId) }),
      client.invalidateQueries({ queryKey: queryKeys.databases.list(workspaceId) }),
      client.invalidateQueries({ queryKey: queryKeys.documents.list(workspaceId) }),
    ]);
}

export interface GroupAnchor {
  after_id?: string;
  before_id?: string;
}

export function useCreateGroup(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: (
      input: {
        name: string;
        section_id: string | null;
        parent_group_id: string | null;
        members?: { kind: GroupedKind; id: string }[];
      } & GroupAnchor,
    ) => api.post<WorkspaceGroup>(`/v1/workspaces/${workspaceId}/groups`, input),
    onSettled: () => refresh(),
  });
}

export function useRenameGroup(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; expected_version?: number }) =>
      api.patch<WorkspaceGroup>(`/v1/groups/${id}`, body),
    onSettled: () => refresh(),
  });
}

/** The workspace labels on a group itself: the full list, in display order. */
export function useSetGroupLabels(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: ({ id, labelIds }: { id: string; labelIds: string[] }) =>
      api.put<WorkspaceGroup>(`/v1/groups/${id}/labels`, { label_ids: labelIds }),
    onSuccess: () => refresh(),
  });
}

export function useMoveGroup(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: { id: string; section_id: string | null; parent_group_id: string | null } & GroupAnchor) =>
      api.post<WorkspaceGroup>(`/v1/groups/${id}/move`, body),
    onSettled: () => refresh(),
  });
}

/** Contents move to the containing level; nothing is deleted. */
export function useDissolveGroup(workspaceId: string) {
  const refresh = useRefresh(workspaceId);
  return useMutation({
    mutationFn: (id: string) => api.delete(`/v1/groups/${id}`),
    onSettled: () => refresh(),
  });
}
